import React, { useCallback, useEffect, useState } from "react";
import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase.js";
import { toast } from "react-toastify";

function AdminSmsPage({ isMobile }) {
  const [message, setMessage] = useState("");
  const [recipientScope, setRecipientScope] = useState("all");
  const [lotIds, setLotIds] = useState("");
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "sms_alert_log"), orderBy("createdAt", "desc"), limit(20)));
      setLogs(snap.docs.map((doc) => {
        const row = doc.data();
        return {
          id: doc.id,
          message: row.message || "",
          status: row.status || "",
          liveMode: !!row.liveMode,
          sentCount: row.sentCount || 0,
          recipientScope: row.recipientScope || "all",
          createdAt: row.createdAt?.toDate?.()?.toLocaleString?.() || "—",
        };
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSaving(true);
    try {
      const res = await httpsCallable(functions, "submitSmsAlert")({
        message: message.trim(),
        recipientScope,
        lotIds: recipientScope === "lots"
          ? lotIds.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      });
      toast.success(res.data?.message || "SMS alert submitted");
      setMessage("");
      loadLogs();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`admin-sms-page ${isMobile ? "admin-sms-page-mobile" : ""}`}>
      <header className="community-services-hero">
        <p className="community-services-eyebrow">Admin communications</p>
        <h1>SMS Text Alerts</h1>
        <p>Send urgent texts via Twilio when configured; otherwise intent is logged for audit.</p>
      </header>

      <form className="community-services-form" onSubmit={handleSubmit}>
        <textarea
          placeholder="Urgent message (keep under 160 characters when possible)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          maxLength={320}
          required
        />
        <select value={recipientScope} onChange={(e) => setRecipientScope(e.target.value)}>
          <option value="all">All owners with phone on file</option>
          <option value="lots">Specific lots</option>
        </select>
        {recipientScope === "lots" ? (
          <input
            placeholder="Lot IDs, comma-separated"
            value={lotIds}
            onChange={(e) => setLotIds(e.target.value)}
          />
        ) : null}
        <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>
          {saving ? "Sending…" : "Send SMS alert"}
        </button>
      </form>

      <section className="community-services-panel">
        <h2>Recent alerts</h2>
        {loading ? <p>Loading…</p> : null}
        <div className="community-services-list">
          {logs.map((log) => (
            <article key={log.id} className="community-services-card">
              <h3>{log.status} {log.liveMode ? "(live)" : "(stub)"}</h3>
              <p>{log.message}</p>
              <p>{log.createdAt} · {log.recipientScope} · sent: {log.sentCount}</p>
            </article>
          ))}
          {logs.length === 0 && !loading ? <p>No SMS alerts logged yet.</p> : null}
        </div>
      </section>
    </div>
  );
}

export default AdminSmsPage;